import { Client, Room } from 'colyseus.js';
import {
    PlayerState,
    TileType,
    ChunkData,
} from '@blockworld/shared';

// ============================================================
// NETWORK CLIENT
// Client-authoritative position sync
// ============================================================

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

// Position update sent to server
export interface PositionUpdate {
    x: number;
    y: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
    isHoldingBlock: boolean;
    heldBlockType: number;
    timestamp: number;
    sequence: number;
}

// Position snapshot received for other players
export interface PlayerSnapshot {
    id: string;
    x: number;
    y: number;
    state: PlayerState;
    facing: number;
    isCrouching: boolean;
    isHoldingBlock: boolean;
    heldBlockType: number;
    timestamp: number;
    sequence: number;
}

export class NetworkClient {
    private client: Client;
    private room: Room | null = null;
    private serverUrl: string;
    private positionSequence = 0;
    private playerCount = 0; // Track player count locally

    // Callbacks
    onConnectionChange: ((status: ConnectionStatus) => void) | null = null;
    onPlayerCountChange: ((count: number) => void) | null = null;
    onChunkData: ((chunks: ChunkData[]) => void) | null = null;
    onBlockUpdate: ((updates: { 
        x: number; 
        y: number; 
        tileType: number; 
        action: string;
        playerId?: string;
        playerX?: number;
        playerY?: number;
        playerState?: PlayerState;
        playerFacing?: number;
        playerIsCrouching?: boolean;
        playerIsHoldingBlock?: boolean;
        playerHeldBlockType?: number;
    }[]) => void) | null = null;
    onBlockActionRollback: ((data: { action: string; x: number; y: number; blockType?: number; reason: string }) => void) | null = null;
    
    // Position-based callbacks
    onPlayerPositions: ((snapshots: PlayerSnapshot[]) => void) | null = null;
    onPlayerJoin: ((player: PlayerSnapshot) => void) | null = null;
    onPlayerLeave: ((playerId: string) => void) | null = null;
    onInitialState: ((localId: string, seed: number, players: PlayerSnapshot[]) => void) | null = null;
    
    // Air strafe callback (immediate horizontal movement)
    onAirStrafe: ((playerId: string, dir: number) => void) | null = null;

    constructor(serverUrl: string) {
        this.serverUrl = serverUrl;
        this.client = new Client(serverUrl);
    }

    async connect(): Promise<void> {
        this.onConnectionChange?.('connecting');

        try {
            this.room = await this.client.joinOrCreate('game');
            this.setupRoomListeners();
            this.onConnectionChange?.('connected');
        } catch (error) {
            console.error('Failed to connect:', error);
            this.onConnectionChange?.('error');
            throw error;
        }
    }

    private setupRoomListeners(): void {
        if (!this.room) return;

        // Receive initial state
        this.room.onMessage('init', (data: { 
            localId: string; 
            seed: number; 
            players: PlayerSnapshot[];
        }) => {
            this.playerCount = data.players.length;
            this.onPlayerCountChange?.(this.playerCount);
            this.onInitialState?.(data.localId, data.seed, data.players);
        });

        // Receive batched position updates for all other players
        this.room.onMessage('positions', (snapshots: PlayerSnapshot[]) => {
            this.onPlayerPositions?.(snapshots);
        });

        // Player joined
        this.room.onMessage('playerJoin', (player: PlayerSnapshot) => {
            this.playerCount++;
            this.onPlayerCountChange?.(this.playerCount);
            this.onPlayerJoin?.(player);
        });

        // Player left
        this.room.onMessage('playerLeave', (playerId: string) => {
            this.playerCount--;
            this.onPlayerCountChange?.(this.playerCount);
            this.onPlayerLeave?.(playerId);
        });

        // Chunk data
        this.room.onMessage('chunkData', (chunks: ChunkData[]) => {
            this.onChunkData?.(chunks);
        });

        // Block updates
        this.room.onMessage('blockUpdate', (updates: { x: number; y: number; tileType: number; action: string }[]) => {
            this.onBlockUpdate?.(updates);
        });

        // Block action rollback (race condition resolution)
        this.room.onMessage('blockActionRollback', (data: { action: string; x: number; y: number; blockType?: number; reason: string }) => {
            this.onBlockActionRollback?.(data);
        });
        
        // Air strafe from other player (immediate horizontal movement)
        this.room.onMessage('airStrafe', (data: { playerId: string; dir: number }) => {
            this.onAirStrafe?.(data.playerId, data.dir);
        });

        // Handle disconnect
        this.room.onLeave((code) => {
            console.log('Disconnected with code:', code);
            this.onConnectionChange?.('disconnected');
        });

        this.room.onError((code, message) => {
            console.error('Room error:', code, message);
            this.onConnectionChange?.('error');
        });
    }

    // Send local player's authoritative position
    sendPosition(
        x: number,
        y: number,
        state: PlayerState,
        facing: number,
        isCrouching: boolean,
        isHoldingBlock: boolean,
        heldBlockType: TileType | null
    ): void {
        if (!this.room) return;

        this.positionSequence++;
        
        const update: PositionUpdate = {
            x,
            y,
            state,
            facing,
            isCrouching,
            isHoldingBlock,
            heldBlockType: heldBlockType ?? -1,
            timestamp: Date.now(),
            sequence: this.positionSequence,
        };

        this.room.send('position', update);
    }

    // Send block action (pickup/place)
    sendBlockAction(
        action: 'pickup' | 'place', 
        x: number, 
        y: number, 
        blockType: TileType,
        playerX: number,
        playerY: number,
        playerState: PlayerState,
        playerFacing: number,
        playerIsCrouching: boolean,
        playerIsHoldingBlock: boolean,
        playerHeldBlockType: number
    ): void {
        if (!this.room) return;
        this.room.send('blockAction', { 
            action, 
            x, 
            y, 
            blockType,
            playerX,
            playerY,
            playerState,
            playerFacing,
            playerIsCrouching,
            playerIsHoldingBlock,
            playerHeldBlockType,
        });
    }

    subscribeToChunks(chunks: { cx: number; cy: number }[]): void {
        if (!this.room) return;
        this.room.send('subscribeChunks', chunks);
    }
    
    // Send air strafe (immediate horizontal movement while airborne)
    sendAirStrafe(dir: number): void {
        if (!this.room) return;
        this.room.send('airStrafe', { dir });
    }

    getLocalPlayerId(): string | null {
        return this.room?.sessionId ?? null;
    }

    disconnect(): void {
        this.room?.leave();
        this.room = null;
    }
}
